/**
 * WebSocket connection with promise-based requests and an event bus for
 * server-pushed messages.
 */
/**
 * Reconnect backoff: immediately, then 1s, 2s, 4s, 8s, then every 15s.
 *
 * The first attempt is immediate because much the most common reason to be here
 * is a server that has just been replaced and is already listening again - the
 * tier 3 handoff holds the port open throughout, so the retry that arrives
 * straight away is the one that succeeds. A server that is genuinely down costs
 * one wasted attempt for that, and the waits after it are unchanged.
 */
const BACKOFF_MS = [0, 1000, 2000, 4000, 8000, 15000];

export class Connection {
  constructor(url) {
    this.url = url;
    this.ws = null;
    this.nextId = 1;
    this.pending = new Map();
    this.listeners = new Map();
    this.state = 'idle';
    /** Set false by close(), so a deliberate disconnect does not reconnect. */
    this.shouldReconnect = true;
    this.attempt = 0;
    this.retryTimer = null;
    this.everOpened = false;
  }

  on(type, handler) {
    if (!this.listeners.has(type)) this.listeners.set(type, new Set());
    this.listeners.get(type).add(handler);
    return () => this.listeners.get(type)?.delete(handler);
  }

  emit(type, data) {
    for (const handler of this.listeners.get(type) ?? []) {
      try {
        handler(data);
      } catch (err) {
        console.error(`listener for ${type} failed`, err);
      }
    }
    for (const handler of this.listeners.get('*') ?? []) {
      try {
        handler({ type, data });
      } catch {
        /* ignore */
      }
    }
  }

  connect() {
    return new Promise((resolve, reject) => {
      this.state = 'connecting';
      this.emit('state', { state: 'connecting' });
      const ws = new WebSocket(this.url);
      this.ws = ws;
      let settled = false;

      ws.onopen = () => {
        settled = true;
        const reopened = this.everOpened;
        this.state = 'open';
        this.attempt = 0;
        this.everOpened = true;
        this.emit('state', { state: 'open', reopened });
        // Let the app restore its session (identity, rooms) after a drop.
        if (reopened) this.emit('reopen', {});
        resolve();
      };

      ws.onmessage = (event) => {
        let frame;
        try {
          frame = JSON.parse(event.data);
        } catch {
          return;
        }
        if (frame.id !== undefined && frame.id !== null && this.pending.has(frame.id)) {
          const { resolve: res, reject: rej } = this.pending.get(frame.id);
          this.pending.delete(frame.id);
          if (frame.ok) res(frame.data);
          else rej(Object.assign(new Error(frame.error?.message ?? 'Request failed'), frame.error ?? {}));
          return;
        }
        if (frame.type) this.emit(frame.type, frame.data ?? {});
      };

      ws.onclose = (event) => {
        this.state = 'closed';
        for (const [, entry] of this.pending) {
          entry.reject(new Error('Connection closed'));
        }
        this.pending.clear();
        this.emit('state', { state: 'closed', code: event.code, reason: event.reason });

        if (!settled) {
          settled = true;
          reject(new Error('Could not connect to the chat server.'));
          return;
        }
        // Codes 4003/4004 are a kick and a ban: reconnecting would be pointless
        // and would spam the server, so those are left to the app to handle.
        if (this.shouldReconnect && event.code !== 4003 && event.code !== 4004) {
          /*
           * 1012 is "service restart", which the server sends when it is being
           * replaced by a newer one that is already up and waiting. Backing off
           * from that would be waiting for something that has already happened,
           * so the attempt counter is reset and the first retry is immediate -
           * which turns a deploy from a few seconds of "connection lost" into a
           * blink. Everything else keeps the usual backoff, because everything
           * else might be a server that is genuinely not there.
           */
          if (event.code === 1012) this.attempt = 0;
          this.scheduleReconnect();
        }
      };

      ws.onerror = () => {
        if (!settled) {
          settled = true;
          this.state = 'error';
          reject(new Error('Could not connect to the chat server.'));
        }
      };
    });
  }

  /**
   * Queues another connection attempt with exponential backoff.
   * Emits `state: 'reconnecting'` with the delay so the UI can count down.
   */
  scheduleReconnect() {
    if (!this.shouldReconnect || this.retryTimer) return;
    const delay = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)];
    this.attempt += 1;
    this.emit('state', { state: 'reconnecting', delay, attempt: this.attempt });

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (!this.shouldReconnect) return;
      this.connect().catch(() => {
        // connect() rejected: onclose has already queued the next attempt, but
        // a failure before the socket opened needs one scheduled here.
        if (!this.retryTimer) this.scheduleReconnect();
      });
    }, delay);
  }

  /** Retries immediately, cancelling any pending backoff wait. */
  retryNow() {
    if (this.state === 'open') return Promise.resolve();
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.shouldReconnect = true;
    this.attempt = 0;
    return this.connect().catch(() => this.scheduleReconnect());
  }

  get isOpen() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /** Sends a request and waits for its reply. */
  request(type, data = {}, { timeoutMs = 20000 } = {}) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('Not connected.'));
    }
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pending.has(id)) {
          this.pending.delete(id);
          reject(new Error(`Request "${type}" timed out.`));
        }
      }, timeoutMs);

      this.pending.set(id, {
        resolve: (value) => {
          clearTimeout(timer);
          resolve(value);
        },
        reject: (err) => {
          clearTimeout(timer);
          reject(err);
        },
      });
      this.ws.send(JSON.stringify({ id, type, data }));
    });
  }

  /** Fire-and-forget send. */
  notify(type, data = {}) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, data }));
    }
  }

  /** Closes for good: no reconnect attempts follow. */
  close() {
    this.shouldReconnect = false;
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    try {
      this.ws?.close(1000, 'client closing');
    } catch {
      /* ignore */
    }
  }
}

export default Connection;
